import type { MessageKey, Role, SignInFailure } from '@ralph/contracts'
import type { I18n } from '@/i18n/index.ts'
import type { Messages } from '@/i18n/messages/en/index.ts'

/**
 * The pure half of the people plane (PRD-21): the rules that decide WHICH sentence, kept out of
 * the catalogue that holds the sentences themselves (PRD-27 LOC-017, D-25).
 *
 * The split is not tidiness. A rank ladder or a refusal classifier copied into a second locale
 * could drift there — and a `pt-BR` file whose ladder lost a rung would change what an
 * administrator is told they are granting, for Portuguese readers only, with nothing failing.
 * Words belong to a locale; rules belong to the product.
 *
 * Every function here takes the catalogue as a PARAMETER rather than importing one. A module
 * that reached for the active catalogue would have to capture it at import, which freezes the
 * language on a switch with no type error, no runtime error and no failing test (F-3).
 * `components/memory/memory-grouping.ts` is the in-repo model.
 */

type PeopleMessages = Messages['people']

// ---------------------------------------------------------------------------
// The ladder
// ---------------------------------------------------------------------------

/**
 * The rank ladder, so the disclosure is generated rather than written four times.
 *
 * This is `ROLE_RANK`'s order — the rule `hasAtLeast` actually applies — and it is a fact about
 * the platform rather than about a language, which is why it cannot live in a message file.
 */
export const ROLE_LADDER: Role[] = ['user', 'auditor', 'operator', 'admin']

/** The highest role someone holds, by name — what the account card under the navigation shows. */
export const highestRoleLabel = (t: PeopleMessages, roles: readonly Role[]): string =>
  t.roleLabel[ROLE_LADDER.findLast((role) => roles.includes(role)) ?? 'user']

/**
 * "Operator also includes everything auditor can do."
 *
 * Generated from the ladder rather than written per role, so it cannot drift from `ROLE_RANK`
 * — which nothing in the product told an administrator about before this screen existed. The
 * ladder is computed here; the sentence, and the agreement it needs, belong to the catalogue.
 */
export const rankDisclosure = (
  fmt: Pick<I18n, 'locale' | 'formatCount' | 'formatList' | 't'>,
  role: Role
): string | null => {
  const t = fmt.t.people
  const index = ROLE_LADDER.indexOf(role)
  const beneath = ROLE_LADDER.slice(1, index).map((lower) => t.roleInSentence[lower])
  if (beneath.length === 0) return null

  return t.rankDisclosure(fmt, role, beneath)
}

// ---------------------------------------------------------------------------
// Why a sign-in was refused
// ---------------------------------------------------------------------------

/**
 * Why a sign-in was refused, as a cause rather than as the sentence the server happened to write.
 *
 * This used to read `failure.reason` — the English `identity-service.ts` threw, stored verbatim
 * in an audit row — and pick the remedy by matching two fragments of it. That is F-11's pattern
 * and it fails in both directions at once: reword the throw and every refusal falls silently
 * through to the least actionable sentence, translate the product and it falls through for
 * every reader. Nothing errors either time. Half of it had already stopped matching.
 *
 * The three things that had to land for this to become a lookup have landed. The two throws in
 * `identity-service.ts` say their key (`auth.account_deactivated`,
 * `auth.email_claimed_by_other_provider`), the two audit writes in `routes/auth.ts` store that
 * key beside the prose at the moment of the refusal, and `SignInFailureSchema` carries it out.
 * So the cause is now read from an identifier that was written down when the refusal happened,
 * which is the only place an audit row can ever get one: a row is never re-rendered.
 *
 * Three causes, not one per key, because the reader gets one of three REMEDIES. The eleven
 * distinct provider faults collapse into "the provider refused it" for the same reason the
 * contract collapses them, and `reason` keeps every distinction for the operator who has to fix
 * the app registration.
 */
export type SignInRefusalCause = 'deactivated' | 'provider_conflict' | 'unknown'

/**
 * `satisfies Partial<Record<MessageKey, …>>` rather than a bare object: a key retired from the
 * contract stops compiling here instead of quietly never matching again — which is the failure
 * mode the prose match had, moved to where the compiler can see it.
 */
const REFUSAL_CAUSE = {
  'auth.account_deactivated': 'deactivated',
  'auth.email_claimed_by_other_provider': 'provider_conflict'
} satisfies Partial<Record<MessageKey, SignInRefusalCause>>

/**
 * `messageKey` is `null` on rows written before the key was stored, and may hold a key this
 * build has not learned. Both mean the same thing here — say the neutral sentence — which is
 * why the lookup is widened to `string` rather than narrowed to `MessageKey`.
 */
export const signInRefusalCause = (failure: Pick<SignInFailure, 'messageKey'>): SignInRefusalCause =>
  (REFUSAL_CAUSE as Record<string, SignInRefusalCause | undefined>)[failure.messageKey ?? ''] ?? 'unknown'

// ---------------------------------------------------------------------------
// What was done to an account
// ---------------------------------------------------------------------------

/**
 * One history line's sentence. Group events name the group they were about (the audit row carries
 * its name, which outlives a deleted group); anything the catalogue does not know yet is said
 * neutrally rather than as its code.
 *
 * The lookup is widened to `string | undefined` on purpose: `action` is whatever the audit table
 * holds, while the catalogue's own keys stay exact so that a missing line in `pt-BR` is a compile
 * error rather than a blank (LOC-011).
 */
export const historySentence = (t: PeopleMessages, action: string, detail: Record<string, unknown>): string => {
  const group =
    typeof detail.groupName === 'string' && detail.groupName !== '' ? detail.groupName : t.historyGroup.unnamed
  switch (action) {
    case 'group.member.added':
      return t.historyGroup.added(group)
    case 'group.member.removed':
      return t.historyGroup.removed(group)
    case 'group.manager.appointed':
      return t.historyGroup.appointed(group)
    case 'group.manager.removed':
      return t.historyGroup.stoppedManaging(group)
    case 'access.role.retired':
      return t.historyRetired
    default:
      return (t.history as Record<string, string | undefined>)[action] ?? t.historyUnknown
  }
}

/**
 * Roles by their names — never the enum; a role that no longer exists is "a retired role".
 *
 * A comma, not `formatList`: this is a before/after readout of two sets either side of an arrow,
 * not a phrase a language joins with a conjunction.
 */
export const historyChange = (t: PeopleMessages, before: string[], after: string[]): string => {
  const named = (role: string): string => (role in t.roleLabel ? t.roleLabel[role as Role] : t.retiredRole)
  return `${before.map(named).join(', ')} → ${after.map(named).join(', ')}`
}
